import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type Asset, type Entry } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { HEXAGON_SIZE, QR_CODE_SIZE } from '../../utils';

export const useScanToPayContent = () => {
  const { formatMessage } = useIntl();
  const contentfulResponse = useContentfulContentTypeEntry<ScanToPayContent>({
    contentType: 'loyalty',
  });

  return useMemo(() => {
    const {
      scanToPayScanAndPayTitle,
      scanToPayScanOnlyTitle,
      scanToPayNoPaymentMethod,
      homeProgressBenefitsDisclaimer,
      scanToPayFaq,
      scanToPayHexagonSize,
      scanToPayQrCodeSize,
    } = contentfulResponse.data?.fields ?? {};

    // ─── Empty State ─────────────────────────────────────────────

    const emptyStateImageUrl =
      scanToPayNoPaymentMethod?.fields?.image?.fields.file.url;
    const emptyStateDescription = scanToPayNoPaymentMethod?.fields?.description;

    // ─── FAQ ─────────────────────────────────────────────────────

    const faq =
      (scanToPayFaq
        ?.map((entry) => ({
          title: entry.fields.title,
          image: entry.fields.image?.fields.file.url,
        }))
        .filter((entry) => entry.title && entry.image) as FaqEntryResult[]) ??
      FALLBACK_FAQ;

    const fallbackScanAndPayTitle = formatMessage(messages.scanAndPayTitle).replaceAll('/br/', '\n'); // prettier-ignore
    const fallbackScanOnlyTitle = formatMessage(messages.scanOnlyTitle).replaceAll('/br/', '\n'); // prettier-ignore

    const hexagonSize = scanToPayHexagonSize ?? HEXAGON_SIZE;
    const qrCodeSize = scanToPayQrCodeSize ?? QR_CODE_SIZE;

    return {
      isFetchingContent: contentfulResponse.fetching,
      scanAndPayTitle: scanToPayScanAndPayTitle ?? fallbackScanAndPayTitle, // prettier-ignore
      scanOnlyTitle: scanToPayScanOnlyTitle ?? fallbackScanOnlyTitle, // prettier-ignore
      emptyStateImageUrl: emptyStateImageUrl,
      emptyStateDescription: emptyStateDescription ?? formatMessage(messages.emptyStateDescription), // prettier-ignore
      benefitsDisclaimer: homeProgressBenefitsDisclaimer,
      faq,
      hexagonSize,
      qrCodeSize,
    };
  }, [
    contentfulResponse.fetching,
    contentfulResponse.data?.fields,
    formatMessage,
  ]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FALLBACK_FAQ: FaqEntryResult[] = [
  {
    title:
      'Scan your QR code at checkout to earn 10 points for every eligible $1 spent in-store',
    image:
      '//images.ctfassets.net/wme4s8lvzccr/15LLyVKCemxcDReNN1UVzR/b02a3cd69dc27f9f36eb77c89b296858/1.svg',
  },
  {
    title: 'Tell the team member which  reward you’d like to apply',
    image:
      '//images.ctfassets.net/wme4s8lvzccr/1ovLPNkiUByztR9Xs45VIs/1272e9f1ca9cc744f2a6c589050bb429/2.svg',
  },
  {
    title: 'Any points earned in-store will be added to your account shortly',
    image:
      '//images.ctfassets.net/wme4s8lvzccr/sbLXnGFxYSYTtLPjLTGnE/7cd1487d4f75ca6cc3ccfbaa0b5e47ff/3.svg',
  },
];

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  scanAndPayTitle: {
    defaultMessage: 'Pay, earn points, and /br/redeem an eligible reward.',
    description: 'Scan To Pay | QR Code | Title',
  },
  scanOnlyTitle: {
    defaultMessage:
      'Earn points and redeem an /br/eligible reward. Pay your way.',
    description: 'Scan To Pay | QR Code | Title',
  },
  emptyStateDescription: {
    defaultMessage:
      'Your unique QR code will enable you to  pay, earn points, and redeem an eligible reward at the register.',
    description: 'Scan To Pay | Empty State | Description',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayContent = {
  scanToPayScanAndPayTitle: string;
  scanToPayScanOnlyTitle: string;
  homeProgressBenefitsDisclaimer: string;
  scanToPayFaq: Entry<FaqEntry>[];
  scanToPayNoPaymentMethod: Entry<{
    image?: Asset;
    description?: string;
  }>;
  scanToPayHexagonSize: number;
  scanToPayQrCodeSize: number;
};

type FaqEntry = {
  title?: string;
  image?: Asset;
};

type FaqEntryResult = {
  title: string;
  image: string;
};
